import { Controller } from "@hotwired/stimulus"

// Client-side search + sort for the categories index. The full list is small
// and server-rendered; this only filters/reorders the existing cards, so the
// page stays crawlable and works without JS (controls degrade to no-ops).
export default class extends Controller<HTMLElement> {
  static targets = [
    "input",
    "clear",
    "grid",
    "card",
    "adRow",
    "empty",
    "resultsInfo",
    "resultsCount",
    "resultsLabel",
    "sortLabel",
    "sortOption",
  ]

  declare readonly inputTarget: HTMLInputElement
  declare readonly clearTarget: HTMLElement
  declare readonly hasClearTarget: boolean
  declare readonly gridTarget: HTMLElement
  declare readonly cardTargets: HTMLElement[]
  declare readonly adRowTargets: HTMLElement[]
  declare readonly emptyTarget: HTMLElement
  declare readonly resultsInfoTarget: HTMLElement
  declare readonly resultsCountTarget: HTMLElement
  declare readonly resultsLabelTarget: HTMLElement
  declare readonly sortLabelTarget: HTMLElement
  declare readonly sortOptionTargets: HTMLElement[]

  filter() {
    const query = this.inputTarget.value.trim().toLowerCase()
    let visible = 0

    for (const card of this.cardTargets) {
      const haystack = card.dataset.searchText ?? ""
      const matches = haystack.includes(query)
      card.classList.toggle("hidden", !matches)
      if (matches) visible += 1
    }

    // Ad rows keep their every-12-cards rhythm only against the full grid;
    // a filtered list would show them stacked between a handful of cards.
    for (const row of this.adRowTargets) row.classList.toggle("hidden", Boolean(query))

    this.emptyTarget.classList.toggle("hidden", visible > 0)
    this.resultsInfoTarget.classList.toggle("hidden", query === "")
    this.resultsCountTarget.textContent = String(visible)
    this.resultsLabelTarget.textContent =
      visible === 1
        ? (this.resultsLabelTarget.dataset.one ?? "")
        : (this.resultsLabelTarget.dataset.other ?? "")
    if (this.hasClearTarget) this.clearTarget.classList.toggle("hidden", query === "")
  }

  clearSearch() {
    this.inputTarget.value = ""
    this.filter()
    this.inputTarget.focus()
  }

  sort(event: Event) {
    const option = event.currentTarget as HTMLElement
    const sortBy = option.dataset.sort

    const cards = [...this.cardTargets]
    cards.sort((a, b) => {
      if (sortBy === "alphabetical") {
        return (a.dataset.name ?? "").localeCompare(b.dataset.name ?? "")
      }
      return Number(b.dataset.count ?? 0) - Number(a.dataset.count ?? 0)
    })
    cards.forEach((card) => this.gridTarget.appendChild(card))

    // Re-appending the cards leaves the ad rows clumped at the grid's start;
    // slot each one back in after every 12th card.
    this.adRowTargets.forEach((row, index) => {
      const anchor = cards[(index + 1) * 12]
      if (anchor) this.gridTarget.insertBefore(row, anchor)
    })

    this.sortLabelTarget.textContent = option.dataset.label ?? ""
    this.sortOptionTargets.forEach((target) => {
      const active = target === option
      target.classList.toggle("item-active", active)
      target.setAttribute("aria-pressed", String(active))
    })

    if (document.activeElement instanceof HTMLElement) document.activeElement.blur()
  }
}
